// Filterleiste der Berichte (Agent 7A, R-03 bis R-05): Zeitraum, Vergleich, Leads (Smart View), Nutzer. Wirkt über die Adresse
// (`zustand.ts`). Ohne das Recht `berichte_alle` entfällt die Nutzerwahl, es zählen nur die eigenen Zahlen.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { Vergleich } from "../api/berichte";
import { useNutzer } from "../auth/AuthContext";
import { Auswahl, type Option } from "../components/Auswahl";
import { Ausklapp } from "../components/Ausklapp";
import { Icon, type IconName } from "../components/Icon";
import { MenueKnopf } from "../components/Menue";
import { useMeta } from "../daten/meta";
import { datumText, heute } from "../werkzeuge/zeit";
import { BT } from "./texte";
import { tageFuer, ZEITRAUM_GRUPPEN, zeitraumText, type ZeitraumWahl } from "./zeitraum";
import type { useBerichtFilter } from "./zustand";

type Filter = ReturnType<typeof useBerichtFilter>;

export function darfAlleSehen(permissions: string[] | undefined): boolean {
  return !!permissions?.includes("berichte_alle");
}

function Pille({ icon, text, an }: { icon: IconName; text: ReactNode; an: boolean }) {
  return (
    <>
      <Icon name={icon} groesse={14} />
      <span className="bericht-pille__text">{text}</span>
      <Icon name="chevron-unten" groesse={12} className={an ? "" : "bericht-pille__pfeil"} />
    </>
  );
}
const pilleKlasse = (an: boolean) => `filterchip filterchip--auswahl bericht-pille${an ? " filterchip--an" : ""}`;

function ZeitraumKnopf({ wahl, onWahl }: { wahl: ZeitraumWahl; onWahl: (w: ZeitraumWahl) => void }) {
  const [offen, setOffen] = useState(false);
  const [knopf, setKnopf] = useState<HTMLButtonElement | null>(null);
  const tage = tageFuer(wahl);
  const [von, setVon] = useState(tage.von);
  const [bis, setBis] = useState(tage.bis);
  useEffect(() => {
    if (offen) {
      setVon(tage.von);
      setBis(tage.bis);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);
  const eigenOk = !!von && !!bis && von <= bis;
  return (
    <>
      <button ref={setKnopf} type="button" className={pilleKlasse(true)} aria-haspopup="dialog" aria-expanded={offen} title="Zeitraum wählen" onClick={() => setOffen((o) => !o)}>
        <Pille icon="kalender" an text={zeitraumText(wahl)} />
      </button>
      <Ausklapp anker={knopf} offen={offen} onSchliessen={() => setOffen(false)} breite={460} rolle="dialog" maxHoehe={480}>
        <div className="bericht-zeitraum">
          <div className="bericht-zeitraum__spalten">
            {ZEITRAUM_GRUPPEN.map((g) => (
              <div key={g.titel} className="bericht-zeitraum__spalte">
                <div className="bericht-zeitraum__titel">{g.titel}</div>
                {g.eintraege.map((e) => (
                  <button
                    key={e.art}
                    type="button"
                    className={`bericht-zeitraum__wahl${wahl.art === e.art ? " bericht-zeitraum__wahl--an" : ""}`}
                    onClick={() => {
                      onWahl({ art: e.art });
                      setOffen(false);
                    }}
                  >
                    {e.label}
                    {wahl.art === e.art ? <Icon name="haken" groesse={13} /> : null}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <div className="bericht-zeitraum__eigen">
            <div className="bericht-zeitraum__titel">Eigener Zeitraum</div>
            <div className="bericht-zeitraum__datum">
              <input type="date" className="eingabe eingabe--datum" aria-label="Von" value={von} max={bis || undefined} onChange={(e) => setVon(e.target.value)} />
              <span className="leise">bis</span>
              <input type="date" className="eingabe eingabe--datum" aria-label="Bis" value={bis} min={von || undefined} max={heute()} onChange={(e) => setBis(e.target.value)} />
              <button
                type="button"
                className="knopf knopf--klein knopf--primaer"
                disabled={!eigenOk}
                onClick={() => {
                  onWahl({ art: "eigen", von, bis });
                  setOffen(false);
                }}
              >
                Übernehmen
              </button>
            </div>
            <p className="leise bericht-zeitraum__hinweis">
              Aktuell: {datumText(tage.von)} bis {datumText(tage.bis)}. Tage zählen nach deutscher Zeit, der letzte Tag mit.
            </p>
          </div>
        </div>
      </Ausklapp>
    </>
  );
}

interface Props {
  filter: Filter;
  /** ohne Vergleichsknopf (z. B. Statuswechsel) */
  ohneVergleich?: boolean;
  /** ohne Nutzerwahl (Seite braucht sie nicht) */
  ohneNutzer?: boolean;
  /** zusätzliche Pillen rechts neben den Standardfiltern */
  weitere?: ReactNode;
  /** rechts außen (z. B. Spaltenwahl) */
  rechts?: ReactNode;
}

export function FilterLeiste({ filter, ohneVergleich = false, ohneNutzer = false, weitere, rechts }: Props) {
  const { werte, setze } = filter;
  const { meta, nutzerListe } = useMeta();
  const ich = useNutzer();
  const alle = darfAlleSehen(ich.permissions);

  const viewOptionen: Option[] = useMemo(
    () => (meta?.smart_views ?? []).filter((v) => v.type === "lead").map((v) => ({ wert: v.id, label: v.name })),
    [meta],
  );
  const nutzerOptionen: Option[] = useMemo(
    () => nutzerListe.filter((u) => u.is_active).map((u) => ({ wert: u.close_user_id ?? u.id, label: u.name, unter: u.id === ich.id ? "Ich" : null })),
    [nutzerListe, ich.id],
  );
  const viewName = werte.smartViewId ? viewOptionen.find((v) => v.wert === werte.smartViewId)?.label ?? "Smart View" : BT.alleLeads;
  const nutzerText =
    werte.nutzerIds.length === 0
      ? BT.alleNutzer
      : werte.nutzerIds.length === 1
        ? nutzerListe.find((u) => u.id === werte.nutzerIds[0] || u.close_user_id === werte.nutzerIds[0])?.name ?? "1 Nutzer"
        : `${werte.nutzerIds.length} Nutzer`;
  const vergleichText = werte.vergleich ? `Mit ${werte.vergleich === "vorjahr" ? BT.vorjahr : BT.vorperiode}` : BT.vergleichen;

  return (
    <div className="liste-leiste bericht-filter" role="group" aria-label="Filter">
      <ZeitraumKnopf wahl={werte.wahl} onWahl={(w) => setze({ wahl: w })} />
      {ohneVergleich ? null : (
        <MenueKnopf
          knopfKlasse={pilleKlasse(!!werte.vergleich)}
          titel="Mit einem früheren Zeitraum vergleichen"
          breite={300}
          punkte={(
            [
              [null, BT.keinVergleich],
              ["vorperiode", `${BT.vorperiode} (gleich viele Tage davor)`],
              ["vorjahr", `${BT.vorjahr} (dieselben Tage ein Jahr früher)`],
            ] as [Vergleich | null, string][]
          ).map(([wert, label]) => ({ label, icon: werte.vergleich === wert ? ("haken" as const) : undefined, onClick: () => setze({ vergleich: wert }) }))}
        >
          <Pille icon="zusammen" an={!!werte.vergleich} text={vergleichText} />
        </MenueKnopf>
      )}
      <Auswahl
        optionen={viewOptionen}
        wert={werte.smartViewId}
        onWahl={(w) => setze({ smartViewId: (w as string | null) || null })}
        leerErlaubt
        titel="Nur Leads einer Smart View zählen"
        knopfKlasse={pilleKlasse(!!werte.smartViewId)}
        knopfInhalt={<Pille icon="ansicht" an={!!werte.smartViewId} text={viewName} />}
        breite={300}
      />
      {ohneNutzer ? null : alle ? (
        <Auswahl
          optionen={nutzerOptionen}
          wert={werte.nutzerIds}
          mehrfach
          leerErlaubt
          onWahl={(w) => setze({ nutzerIds: w as string[] })}
          titel="Nach Nutzern filtern"
          knopfKlasse={pilleKlasse(werte.nutzerIds.length > 0)}
          knopfInhalt={<Pille icon="person" an={werte.nutzerIds.length > 0} text={nutzerText} />}
          breite={260}
        />
      ) : (
        <span className="filterchip bericht-pille bericht-pille--fest" title={BT.nurEigeneHinweis}>
          <Icon name="person" groesse={14} />
          <span className="bericht-pille__text">{BT.nurEigene}</span>
        </span>
      )}
      {weitere}
      {rechts ? <div className="bericht-filter__rechts">{rechts}</div> : null}
    </div>
  );
}
